import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from './client'

/**
 * Profile domain — `GET /api/profile`.
 *
 * Lives in its own module rather than auth.ts because /api/profile is a
 * separate backend handler from /api/me (auth.ts owns session identity;
 * profile.ts owns the LoopCV-backed candidate profile, including quota
 * counters and outbound email settings).
 *
 * Quota semantics (mirrored from backend/internal/loopcv/types.go):
 *   - `pricingPlan` is LoopCV's internal plan index (we provision plan 3
 *     for partner-managed candidates; >2 unlocks AutoAnswerQuestions).
 *   - `numRemainingApplications` is `-1` when the plan is unlimited.
 *     Treat the sentinel verbatim in the UI ("Unlimited" pill) — do NOT
 *     translate to 0 / null.
 *   - `numRemainingCvReports` is the CV-report feature counter; same
 *     `-1 = unlimited` convention.
 *   - `platformEmail` is the from-address LoopCV uses on outbound email
 *     applications — distinct from the local login email.
 */
export interface PhoneData {
  formatNational: string
  nationalNumber: string
  countryCallingCode: string
  e164: string
}

export interface Profile {
  userId: string
  email: string
  /** From-address for outbound email applications (distinct from login email). */
  platformEmail: string
  firstName: string
  lastName: string
  phoneData: PhoneData | null
  /** 城市级位置（`City, Country`）；本地保存，LoopCV 侧只存国家。 */
  location: string
  enableEmailsApplications: boolean
  automaticallyAnswerQuestions: boolean
  sendEmailWithoutCV: boolean
  loopCount: number
  /** LoopCV plan index; >2 unlocks AutoAnswerQuestions. */
  pricingPlan: number
  /** `-1` = unlimited; otherwise a literal countdown of applies left. */
  numRemainingApplications: number
  /** `-1` = unlimited; otherwise a literal countdown of CV-report runs. */
  numRemainingCvReports: number
}

export const profileKeys = {
  all: ['profile'] as const,
}

/**
 * Profile query. Cached for 60s; the quota counters tick down on every
 * apply, so a stale-while-revalidate window of one minute is the sweet
 * spot between accuracy and request volume.
 */
export const profileQuery = () =>
  queryOptions({
    queryKey: profileKeys.all,
    queryFn: () => api.get<Profile>('/profile'),
    staleTime: 60 * 1000,
  })

/* -------------------------------------------------------------------------
 * Update (P2.8) — partial PUT /api/profile.
 * ------------------------------------------------------------------------- */

/**
 * profileUpdateInputSchema validates the form payload before we send it.
 *
 * All fields are optional — the backend ProfileUpdate uses pointer types
 * to distinguish "leave unchanged" from "set to zero", so we mirror that
 * by sending only the keys the user actually edited (see useUpdateProfileMutation).
 *
 * Email is the only field we validate format on; everything else is a
 * passthrough toggle / free-text label.
 */
const profileUpdateFieldsSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(120).optional(),
  lastName: z.string().min(1, 'Last name is required').max(120).optional(),
  location: z.string().max(120).optional(),
  email: z.string().email('Must be a valid email address').optional(),
  phoneCountryCallingCode: z
    .string()
    .max(5)
    .refine((value) => value === '' || /^\+?[1-9]\d{0,2}$/.test(value.trim()), {
      message: 'Use a valid calling code, for example +65',
    })
    .optional(),
  phoneNationalNumber: z
    .string()
    .max(30)
    .refine((value) => value === '' || /^[0-9 ().-]+$/.test(value.trim()), {
      message: 'Use a valid phone number',
    })
    .optional(),
  sendEmailWithoutCV: z.boolean().optional(),
  enableEmailsApplications: z.boolean().optional(),
  automaticallyAnswerQuestions: z.boolean().optional(),
  shareMyData: z.boolean().optional(),
  notificationSettings: z
    .object({
      questionNotificationToUsers: z
        .object({
          enabled: z.boolean().optional(),
          channel: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
})

/** 同上的纯 object 版本；表单要替换某个字段（如 location）时用 .omit()。 */
export { profileUpdateFieldsSchema }

export const profileUpdateInputSchema = profileUpdateFieldsSchema.superRefine((value, ctx) => {
  const code = value.phoneCountryCallingCode?.trim() ?? ''
  const number = value.phoneNationalNumber?.trim() ?? ''
  if (!code && !number) return
  if (!code) {
    ctx.addIssue({
      code: 'custom',
      path: ['phoneCountryCallingCode'],
      message: 'Country calling code is required',
    })
  }
  if (!number) {
    ctx.addIssue({
      code: 'custom',
      path: ['phoneNationalNumber'],
      message: 'Phone number is required',
    })
  }
})
export type ProfileUpdateInput = z.infer<typeof profileUpdateInputSchema>

/**
 * PUT /api/profile — partial update. The hook does not pre-filter the
 * body; callers should pass only the fields the user actually changed
 * (dirty-field tracking in the form component) so we never silently
 * clobber a value the user didn't touch.
 *
 * On success we invalidate profileQuery so the header / quota badge
 * re-fetch and pick up server-canonical values.
 */
export function useUpdateProfileMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: ProfileUpdateInput) => api.put<Profile>('/profile', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: profileKeys.all })
    },
  })
}
